import React from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { routes } from "constants/routes";
import { RequireAuth } from "core/routing/RequireAuth";
import { NotRequireAuth } from "core/routing/NotRequireAuth";
import RequireNetwork from "core/routing/RequireNetwork";
import { SEO_CONTENT } from "constants/Seo";
import { RedirectWrapper } from "layouts/RedirectWrapper";
import { OldArticleRedirect } from "core/routing/OldArticleRedirect";
import { CountrySelectProvider } from "contexts/CountrySelectContext";
import { ModalProvider } from "contexts/ModalContext";
import asyncComponentLoader from "core/loader";
import { HelmetWrapper } from "layouts/HelmetWrapper";
import { ConsentLayout } from "layouts/ConsentLayout";
import { PageLoader } from "components/PageLoader";
// import { Welcome } from "pages/Welcome";
// import { ExploreAll } from "pages/Explore/ExploreAll";
// import { GetGuidance } from "pages/Explore/GetGuidance";
// import { Learn } from "pages/Explore/Learn";
// import { Products } from "pages/Explore/Products";
// import { StickyLoader } from "components/StickyLoader";
import { MenuWithOutHeaderLayout } from "layouts/MenuWithOutHeaderLayout";
import { MenuWithHeaderLayout } from "layouts/MenuWithHeaderLayout";
import { Welcome } from "@/pages/Welcome";
import { MarketingConsentLayout } from "@/layouts/MarketingConsentLayout";
// import FcmTest from "@/pages/FcmTest/FcmTest";

const Loader = asyncComponentLoader(() =>
  import("components/Loader").then(({ Loader }) => ({
    default: Loader,
  })),
);
const LoaderComponent: React.FC = () => <Loader loaderText="loading.." />;
const PageSkeltonLoader: React.FC = () => <PageLoader />;
const ErrorView = asyncComponentLoader(() =>
  import("components/ErrorView").then(({ ErrorView }) => ({
    default: ErrorView,
  })),
);
const NotFound = asyncComponentLoader(() =>
  import("pages/NotFound").then(({ NotFound }) => ({
    default: NotFound,
  })),
);
const ExploreAll = asyncComponentLoader(() =>
  import("pages/Explore/ExploreAll").then(({ ExploreAll }) => ({
    default: ExploreAll,
  })),
);
const GetGuidance = asyncComponentLoader(() =>
  import("pages/Explore/GetGuidance").then(({ GetGuidance }) => ({
    default: GetGuidance,
  })),
);
const Learn = asyncComponentLoader(() =>
  import("pages/Explore/Learn").then(({ Learn }) => ({
    default: Learn,
  })),
);
const Products = asyncComponentLoader(() =>
  import("pages/Explore/Products").then(({ Products }) => ({
    default: Products,
  })),
);
const Country = asyncComponentLoader(() =>
  import("pages/Country").then(({ Country }) => ({
    default: Country,
  })),
);
const SplashScreenWrapper = asyncComponentLoader(() =>
  import("layouts/SplashScreenWrapper").then(({ SplashScreenWrapper }) => ({
    default: SplashScreenWrapper,
  })),
);
const RegistrationFlow = asyncComponentLoader(() =>
  import("pages/RegistrationFlow").then(({ RegistrationFlow }) => ({
    default: RegistrationFlow,
  })),
);
const GuidanceDetails = asyncComponentLoader(() =>
  import("pages/GuidanceDetails").then(({ GuidanceDetails }) => ({
    default: GuidanceDetails,
  })),
);

const TrackSymptom = asyncComponentLoader(() =>
  import("pages/TrackSymptom").then(({ TrackSymptom }) => ({
    default: TrackSymptom,
  })),
);

const Home = asyncComponentLoader(
  () => import("pages/Home").then(({ Home }) => ({ default: Home })),
  PageSkeltonLoader,
);

const TapToStart = asyncComponentLoader(
  () =>
    import("pages/TapToStart").then(({ TapToStart }) => ({
      default: TapToStart,
    })),
  PageSkeltonLoader,
);
const Profile = asyncComponentLoader(
  () => import("pages/Profile").then(({ Profile }) => ({ default: Profile })),
  PageSkeltonLoader,
);
const ProfileDetails = asyncComponentLoader(
  () =>
    import("pages/Profile/ProfileDetails").then(({ ProfileDetails }) => ({
      default: ProfileDetails,
    })),
  LoaderComponent,
);
const Notifications = asyncComponentLoader(
  () =>
    import("pages/Profile/Notification").then(({ Notification }) => ({
      default: Notification,
    })),
  LoaderComponent,
);
const Track = asyncComponentLoader(
  () => import("pages/Track").then(({ Track }) => ({ default: Track })),
  PageSkeltonLoader,
);
const Insights = asyncComponentLoader(
  () =>
    import("pages/Insights").then(({ Insights }) => ({ default: Insights })),
  PageSkeltonLoader,
);
const Authenticate = asyncComponentLoader(() =>
  import("pages/Authenticate").then(({ Authenticate }) => ({
    default: Authenticate,
  })),
);
const Explore = asyncComponentLoader(
  () =>
    import("pages/Explore").then(({ Explore }) => ({
      default: Explore,
    })),
  PageSkeltonLoader,
);
const ArticleDetails = asyncComponentLoader(() =>
  import("pages/ArticleDetails").then(({ ArticleDetails }) => ({
    default: ArticleDetails,
  })),
);
const QuickCheckAssessment = asyncComponentLoader(
  () =>
    import("pages/QuickCheck/Assessment").then(({ Assessment }) => ({
      default: Assessment,
    })),
  PageSkeltonLoader,
);
const Calender = asyncComponentLoader(() =>
  import("pages/Track/Calender").then(({ Calender }) => ({
    default: Calender,
  })),
);
const Results = asyncComponentLoader(
  () =>
    import("pages/Results").then(({ Results }) => ({
      default: Results,
    })),
  PageSkeltonLoader,
);
const TrendsBySign = asyncComponentLoader(() =>
  import("pages/Trends/Sign").then(({ Sign }) => ({
    default: Sign,
  })),
);
const ImageUploadPage = asyncComponentLoader(() =>
  import("pages/Profile/ImageUpload").then(({ ImageUpload }) => ({
    default: ImageUpload,
  })),
);
const LogoutPage = asyncComponentLoader(() =>
  import("pages/Logout").then(({ Logout }) => ({
    default: Logout,
  })),
);
const ResetPassword = asyncComponentLoader(() =>
  import("pages/ResetPassword").then(({ ResetPassword }) => ({
    default: ResetPassword,
  })),
);
const ChangePassword = asyncComponentLoader(() =>
  import("pages/ChangePassword").then(({ ChangePassword }) => ({
    default: ChangePassword,
  })),
);
const SetNewPassword = asyncComponentLoader(() =>
  import("pages/SetNewPassword").then(({ SetNewPassword }) => ({
    default: SetNewPassword,
  })),
);
const Faq = asyncComponentLoader(
  () =>
    import("pages/FaqV2").then(({ FaqV2 }) => ({
      default: FaqV2,
    })),
  LoaderComponent,
);
const TermsPrivacy = asyncComponentLoader(
  () =>
    import("pages/TermsPrivacy").then(({ TermsPrivacy }) => ({
      default: TermsPrivacy,
    })),
  LoaderComponent,
);
const PrivacyStatement = asyncComponentLoader(
  () =>
    import("pages/PrivacyStatement").then(({ PrivacyStatement }) => ({
      default: PrivacyStatement,
    })),
  LoaderComponent,
);
const AboutUs = asyncComponentLoader(
  () =>
    import("pages/AboutUs").then(({ AboutUs }) => ({
      default: AboutUs,
    })),
  LoaderComponent,
);
const ContactFormPage = asyncComponentLoader(
  () =>
    import("pages/ContactFormPage").then(({ ContactFormPage }) => ({
      default: ContactFormPage,
    })),
  LoaderComponent,
);

const ProductDetails = asyncComponentLoader(
  () =>
    import("pages/ProductDetails").then(({ ProductDetails }) => ({
      default: ProductDetails,
    })),
  PageSkeltonLoader,
);
const Demo = asyncComponentLoader(() =>
  import("pages/Demo").then(({ Demo }) => ({
    default: Demo,
  })),
);
// const Demo = asyncComponentLoader(() =>
//   import("pages/Demo").then(({ Demo }) => ({
//     default: Demo,
//   })),
// );

const LoginIntro = asyncComponentLoader(() =>
  import("pages/LoginIntro").then(({ LoginIntro }) => ({
    default: LoginIntro,
  })),
);
const SignupIntro = asyncComponentLoader(() =>
  import("pages/SignupIntro").then(({ SignupIntro }) => ({
    default: SignupIntro,
  })),
);
const Login = asyncComponentLoader(() =>
  import("pages/Login").then(({ Login }) => ({
    default: Login,
  })),
);
const Signup = asyncComponentLoader(() =>
  import("pages/Signup").then(({ Signup }) => ({
    default: Signup,
  })),
);

const Intro = asyncComponentLoader(
  () =>
    import("pages/Intro").then(({ Intro }) => ({
      default: Intro,
    })),
  PageSkeltonLoader,
);

function AppRoutes() {
  const routesArray = createBrowserRouter([
    {
      path: "/",
      element: (
        <ModalProvider>
          <CountrySelectProvider>
            <MenuWithOutHeaderLayout />
          </CountrySelectProvider>
        </ModalProvider>
      ),
      errorElement: <ErrorView />,
      children: [
        {
          path: "",
          element: (
            <HelmetWrapper content={SEO_CONTENT.HOME}>
              <NotRequireAuth>
                <Welcome />
              </NotRequireAuth>
            </HelmetWrapper>
          ),
        },
      ],
    },
    {
      path: "/",
      element: (
        <ModalProvider>
          <CountrySelectProvider>
            <MenuWithHeaderLayout />
          </CountrySelectProvider>
        </ModalProvider>
      ),
      errorElement: <ErrorView />,
      children: [
        {
          path: routes.home.path,
          children: [
            {
              path: "",
              element: (
                <HelmetWrapper content={SEO_CONTENT.HOME}>
                  <RequireAuth isModal={false} isFullPage={true}>
                    <RedirectWrapper>
                      <MarketingConsentLayout>
                        <Home />
                      </MarketingConsentLayout>
                    </RedirectWrapper>
                  </RequireAuth>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.tapToStart.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.HOME}>
                  <TapToStart />
                </HelmetWrapper>
              ),
            },
          ],
        },
        {
          path: routes.insights.path,
          children: [
            {
              path: "",
              element: (
                <HelmetWrapper content={SEO_CONTENT.INSIGHTS}>
                  <RequireNetwork>
                    <Insights />
                  </RequireNetwork>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.trendsBySign.path,
              children: [
                {
                  path: "",
                  element: (
                    <RequireAuth isFullPage={true}>
                      <TrendsBySign />
                    </RequireAuth>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: routes.track.path,
          children: [
            {
              path: "",
              element: (
                <HelmetWrapper content={SEO_CONTENT.TRACKER}>
                  <RequireNetwork>
                    <ConsentLayout isShownBeforeAuth>
                      <Track />
                    </ConsentLayout>
                  </RequireNetwork>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.trackSymptom.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.TRACKER}>
                  <RequireAuth>
                    <ConsentLayout>
                      <TrackSymptom />
                    </ConsentLayout>
                  </RequireAuth>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.trackCalender.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.TRACKER}>
                  <RequireAuth>
                    <ConsentLayout>
                      <Calender />
                    </ConsentLayout>
                  </RequireAuth>
                </HelmetWrapper>
              ),
            },
          ],
        },
        {
          path: routes.explore.path,
          children: [
            {
              path: "",
              element: (
                <HelmetWrapper content={SEO_CONTENT.EXPLORE}>
                  <RequireNetwork>
                    <MarketingConsentLayout>
                      <Explore>
                        <ExploreAll />
                      </Explore>
                    </MarketingConsentLayout>
                  </RequireNetwork>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.getGuidance.path,
              children: [
                {
                  path: "",
                  element: (
                    <Navigate to={`${routes.guidanceModule.path}`} replace />
                  ),
                },
                {
                  path: routes.guidanceModule.path,
                  element: (
                    <HelmetWrapper content={SEO_CONTENT.GET_GUIDANCE}>
                      <RequireAuth isModal={true} isFullPage={true}>
                        <MarketingConsentLayout>
                          <Explore>
                            <GetGuidance />
                          </Explore>
                        </MarketingConsentLayout>
                      </RequireAuth>
                    </HelmetWrapper>
                  ),
                },
                {
                  path: `${routes.guidanceModule.path}/${routes.slug.path}`,
                  element: (
                    <HelmetWrapper content={SEO_CONTENT.GET_GUIDANCE}>
                      <RequireAuth isModal={true} isFullPage={true}>
                        <MarketingConsentLayout>
                          <Explore>
                            <GetGuidance />
                          </Explore>
                        </MarketingConsentLayout>
                      </RequireAuth>
                    </HelmetWrapper>
                  ),
                },
                {
                  path: routes.guidanceDetails.path,
                  element: (
                    <RequireAuth isModal={false} isFullPage={true}>
                      <GuidanceDetails />
                    </RequireAuth>
                  ),
                },
              ],
            },
            {
              path: routes.articleDetailsdemo.path,
              element: (
                <OldArticleRedirect>
                  <></>
                </OldArticleRedirect>
              ),
            },
            {
              path: routes.learn.path,
              children: [
                {
                  path: "",
                  element: (
                    <HelmetWrapper content={SEO_CONTENT.LEARN}>
                      <MarketingConsentLayout>
                        <Explore>
                          <Learn />
                        </Explore>
                      </MarketingConsentLayout>
                    </HelmetWrapper>
                  ),
                  // element: <Navigate to="all" replace />,
                },
                {
                  path: routes.slug.path,
                  element: (
                    <HelmetWrapper content={SEO_CONTENT.LEARN}>
                      <MarketingConsentLayout>
                        <Explore>
                          <Learn />
                        </Explore>
                      </MarketingConsentLayout>
                    </HelmetWrapper>
                  ),
                },
              ],
            },
            {
              path: routes.articleDetails.path,
              element: (
                <MarketingConsentLayout>
                  <ArticleDetails />
                </MarketingConsentLayout>
              ),
            },
            {
              path: routes.products.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.PRODUCTS}>
                  <MarketingConsentLayout>
                    <Explore>
                      <Products />
                    </Explore>
                  </MarketingConsentLayout>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.productDetails.path,
              element: (
                <MarketingConsentLayout>
                  <ProductDetails />
                </MarketingConsentLayout>
              ),
            },
          ],
        },
        {
          path: routes.profile.path,
          children: [
            {
              path: "",
              element: (
                <HelmetWrapper content={SEO_CONTENT.PROFILE}>
                  <Profile />
                </HelmetWrapper>
              ),
            },
            {
              path: routes.profileDetails.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.PROFILE_DETAILS}>
                  <RequireAuth isFullPage={true}>
                    <ProfileDetails />
                  </RequireAuth>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.notifications.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.NOTIFICATION}>
                  <RequireAuth isFullPage={true}>
                    <Notifications />
                  </RequireAuth>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.country.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.COUNTRY}>
                  <Country />
                </HelmetWrapper>
              ),
            },
            {
              path: routes.quickCheckAssessment.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.QUIZ}>
                  <QuickCheckAssessment />
                </HelmetWrapper>
              ),
            },
            {
              path: routes.results.path,
              element: (
                <RequireAuth>
                  <ConsentLayout>
                    <Results />
                  </ConsentLayout>
                </RequireAuth>
              ),
            },
            {
              path: routes.registrationFlow.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.REGISTRATION_STEP_ONETWO}>
                  <RequireAuth>
                    <RegistrationFlow />
                  </RequireAuth>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.imageUpload.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.REGISTRATION_STEP_ONETWO}>
                  <RequireAuth>
                    <ImageUploadPage />
                  </RequireAuth>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.resetPassword.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.PROFILE}>
                  <ResetPassword />
                </HelmetWrapper>
              ),
            },
            {
              path: routes.changePassword.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.PROFILE}>
                  <RequireAuth>
                    <ChangePassword />
                  </RequireAuth>
                </HelmetWrapper>
              ),
            },
            {
              path: routes.setNewPassword.path,
              element: (
                <HelmetWrapper content={SEO_CONTENT.PROFILE}>
                  <RequireAuth>
                    <SetNewPassword />
                  </RequireAuth>
                </HelmetWrapper>
              ),
            },
          ],
        },
        {
          path: routes.termsPrivacy.path,
          children: [
            {
              path: "",
              element: (
                <HelmetWrapper content={SEO_CONTENT.CONDITIONS}>
                  <TermsPrivacy />
                </HelmetWrapper>
              ),
            },
          ],
        },
        {
          path: routes.privacyStatement.path,
          children: [
            {
              path: "",
              element: (
                <HelmetWrapper content={SEO_CONTENT.PRIVACY}>
                  <PrivacyStatement />
                </HelmetWrapper>
              ),
            },
          ],
        },
        {
          path: routes.faq.path,
          children: [
            {
              path: "",
              element: (
                <HelmetWrapper content={SEO_CONTENT.FAQ}>
                  <Faq />
                </HelmetWrapper>
              ),
            },
          ],
        },
        {
          path: routes.contactFormPage.path,
          element: (
            <HelmetWrapper content={SEO_CONTENT.CONTACT_US}>
              <ContactFormPage />
            </HelmetWrapper>
          ),
        },
        {
          path: routes.aboutUs.path,
          element: (
            <HelmetWrapper content={SEO_CONTENT.ABOUT_US}>
              <AboutUs />
            </HelmetWrapper>
          ),
        },
        {
          path: routes.intro.path,
          element: (
            <HelmetWrapper content={SEO_CONTENT.INTRO}>
              <Intro />
            </HelmetWrapper>
          ),
        },
        {
          path: routes.loginIntro.path,
          element: <LoginIntro />,
        },
        {
          path: routes.login.path,
          element: <Login />,
        },
        {
          path: routes.signup.path,
          element: <Signup />,
        },
        {
          path: routes.signupIntro.path,
          element: <SignupIntro />,
        },
        {
          path: "*",
          element: (
            <HelmetWrapper content={SEO_CONTENT.NOTFOUND}>
              <NotFound />
            </HelmetWrapper>
          ),
        },
      ],
    },
    // Public layout for those pages not need authentication to access
    {
      path: routes.authenticate.path,
      element: <Authenticate />,
    },
    {
      path: routes.logout.path,
      element: <LogoutPage />,
    },

    // // {
    // //   path: routes.form.path,
    // //   element: <Form />,
    // // },
    {
      path: routes.demo.path,
      element: <Demo />,
      // element: <FcmTest />,
    },
  ]);
  return (
    <SplashScreenWrapper>
      {/* <React.Suspense fallback={<Loader loaderText="Loading" />}> */}
      <RouterProvider router={routesArray} />
      {/* </React.Suspense> */}
    </SplashScreenWrapper>
  );
}
export default AppRoutes;
